import {
  faBoxesPacking,
  faFileExcel,
  faPlus,
  faX,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { colors } from "../../global";

const Surcharge = ({
  metadata,
  handleSurchargeColumnChange,
  removeSurchargeColumn,
  addSurchargeColumn,
  openExcelModal,
  excelSelectableFields,
}) => {
  const fields = ["name", "buy_rate", "sell_rate"];

  const formatLabel = (key) => {
    return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getFieldDescription = (key) => {
    const descriptions = {
      name: "Unique identifier for the Surcharge column",
      target_column: "Excel column to apply Surcharge to",
      Item_Type: "Type of item for categorization",
      from: "Starting Surcharge",
      to: "Ending Surcharge",
      rename_column: "New name for the column",
    };
    return descriptions[key] || "";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
          Surcharge
        </h3>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon
            icon={faBoxesPacking}
            size={16}
            style={{ color: colors.darkGray }}
          />
          <span className="text-sm" style={{ color: colors.darkGray }}>
            {metadata?.surcharges?.length} Surcharge
            {metadata?.surcharges?.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Buffer Column List */}
      <div className="space-y-4">
        {metadata?.surcharges?.map((surcharge, index) => (
          <div
            key={index}
            className="group relative bg-white rounded-xl border border-gray-200 p-6 hover:shadow-sm transition-all duration-200"
          >
            {/* Column Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-semibold"
                  style={{ backgroundColor: colors.primary }}
                >
                  {index + 1}
                </div>
                <div>
                  <span
                    className="text-sm font-medium"
                    style={{ color: colors.primary }}
                  >
                    Surcharge Column {index + 1}
                  </span>
                  {surcharge.name && (
                    <p className="text-xs" style={{ color: colors.darkGray }}>
                      {surcharge.name}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => removeSurchargeColumn(index)}
                className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:bg-red-50"
                style={{ color: colors.error }}
                type="button"
                aria-label="Remove Buffer Column"
              >
                <FontAwesomeIcon icon={faX} size="16" />
              </button>
            </div>

            {/* Input Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fields.map((key) => {
                const isExcelSelectable = excelSelectableFields.includes(key);
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        className="text-sm font-medium"
                        style={{ color: colors.darkGray }}
                      >
                        {formatLabel(key)}
                      </label>
                      {getFieldDescription(key) && (
                        <div
                          className="group/tooltip relative cursor-help"
                          title={getFieldDescription(key)}
                        >
                          <div
                            className="w-4 h-4 rounded-full flex items-center justify-center text-xs"
                            style={{
                              backgroundColor: colors.gray,
                              color: colors.darkGray,
                            }}
                          >
                            ?
                          </div>
                        </div>
                      )}
                    </div>

                    {isExcelSelectable ? (
                      <div className="flex">
                        <input
                          type="text"
                          value={surcharge[key] || ""}
                          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-l-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                          style={{
                            "--tw-ring-color": colors.primary + "40",
                            borderColor: surcharge[key]
                              ? colors.primary + "40"
                              : undefined,
                          }}
                          placeholder={`Enter ${formatLabel(
                            key
                          ).toLowerCase()}`}
                          onChange={(e) =>
                            handleSurchargeColumnChange(
                              index,
                              key,
                              e.target.value
                            )
                          }
                        />
                        {openExcelModal &&
                          (metadata.file_name.endsWith(".csv") ||
                            metadata?.sheet_information?.sheet_name) && (
                            <button
                              onClick={() => openExcelModal(index, key)}
                              type="button"
                              aria-label="Open Excel Sheet"
                              className="px-3 py-2 border border-l-0 border-gray-200 rounded-r-lg hover:bg-gray-50 transition-colors duration-200 flex items-center justify-center"
                              style={{ color: colors.primary }}
                            >
                              <FontAwesomeIcon icon={faFileExcel} size="16" />
                            </button>
                          )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={surcharge[key] || ""}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200"
                        style={{
                          "--tw-ring-color": colors.primary + "40",
                          borderColor: surcharge[key]
                            ? colors.primary + "40"
                            : undefined,
                        }}
                        placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                        onChange={(e) =>
                          handleSurchargeColumnChange(
                            index,
                            key,
                            e.target.value
                          )
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"></div>
            <div className="space-y-2 mt-2">
              <div className="flex items-center justify-between">
                <label
                  className="text-sm font-medium"
                  style={{ color: colors.darkGray }}
                >
                  Description
                </label>
              </div>
              <div className="flex">
                <textarea
                  className="peer h-full min-h-[100px] w-full resize-none rounded-[7px] border border-blue-gray-200 px-3 py-2.5 font-sans text-sm font-normal text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 focus:border-2 focus:border-gray-900 focus:outline-0 disabled:resize-none disabled:border-0 disabled:bg-blue-gray-50"
                  placeholder=" "
                  onChange={(e) =>
                    handleSurchargeColumnChange(
                      index,
                      "description",
                      e.target.value
                    )
                  }
                  value={surcharge["description"] || ""}
                ></textarea>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={addSurchargeColumn}
        className="w-full py-4 px-6 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center gap-3 text-sm font-medium hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 group"
        style={{ color: colors.primary }}
        type="button"
      >
        <FontAwesomeIcon
          icon={faPlus}
          size={18}
          className="group-hover:scale-110 transition-transform duration-200"
        />
        Add Surcharge Column
      </button>
    </div>
  );
};

export default Surcharge;
